import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';

import {
  MasFiltersResponseV1,
  MasMessageV1,
  MasSearchRequestV1,
  MasSearchResponseV1
} from '@core/models/mas-history';
import { isGatewayError, problemDetails, retryAfterSeconds } from '@core/utils/problem-details';
import { environment } from '@src/environments/environment';

/** The message history API's base URL, or null where this environment's gateway does not publish it. */
export const MAS_API_URL = new InjectionToken<string | null>('MAS_API_URL', {
  providedIn: 'root',
  factory: () => (environment.apis.mas ? `${environment.apiUrl}${environment.apis.mas}/v1` : null)
});

export type MasHistoryErrorKind =
  | 'invalid'
  | 'unauthenticated'
  | 'forbidden'
  | 'notFound'
  | 'notServed'
  | 'rateLimited'
  | 'identityUnavailable'
  | 'timeout'
  | 'unavailable';

/** The kinds where the same request can succeed a little later. */
const RETRYABLE_KINDS: ReadonlySet<MasHistoryErrorKind> = new Set<MasHistoryErrorKind>([
  'rateLimited',
  'identityUnavailable',
  'timeout',
  'unavailable'
]);

const UNAVAILABLE = 'Message history unavailable.';

/** A failed history call, as the pages present it: a headline, the server's detail, and whether to offer a retry. */
export class MasHistoryError extends Error {
  constructor(
    readonly kind: MasHistoryErrorKind,
    readonly status: number,
    message: string,
    readonly detail: string | null
  ) {
    super(message);
    this.name = 'MasHistoryError';
  }

  get retryable(): boolean {
    return RETRYABLE_KINDS.has(this.kind);
  }
}

/**
 * A 404 on a message means no message has that id. On the search or the filters, or in the gateway's own shape on any
 * call, it means this environment's gateway does not serve the history.
 */
export type MasHistoryCall = 'search' | 'message' | 'filters';

export function toMasHistoryError(error: unknown, call: MasHistoryCall): MasHistoryError {
  if (error instanceof MasHistoryError) {
    return error;
  }
  if (!(error instanceof HttpErrorResponse)) {
    return new MasHistoryError('unavailable', 0, UNAVAILABLE, null);
  }

  const problem = problemDetails(error);
  const detail = problem.detail ?? null;
  const status = error.status;

  switch (status) {
    case 400:
      return new MasHistoryError(
        'invalid',
        status,
        'The request was rejected.',
        detail ?? problem.title ?? null
      );
    case 401:
      return new MasHistoryError('unauthenticated', status, 'Your session has expired.', detail);
    case 403:
      return new MasHistoryError('forbidden', status, 'The message history refused this request.', detail);
    case 404:
      return call === 'message' && !isGatewayError(error)
        ? new MasHistoryError('notFound', status, 'No message has this id.', detail)
        : new MasHistoryError(
            'notServed',
            status,
            'The message history is not available in this environment.',
            detail
          );
    case 429: {
      const seconds = retryAfterSeconds(error);
      const wait = seconds === null ? detail : `Try again in ${seconds} ${seconds === 1 ? 'second' : 'seconds'}.`;
      return new MasHistoryError('rateLimited', status, 'Too many message history requests.', wait);
    }
    case 502:
      return new MasHistoryError(
        'identityUnavailable',
        status,
        'The history host could not sign in to its database.',
        detail
      );
    case 504:
      return new MasHistoryError('timeout', status, 'The message history timed out.', detail);
  }

  // Below 400 is a response that failed to parse, such as a gateway or dev-server HTML page: nothing answered.
  if (status < 400 || status >= 500) {
    return new MasHistoryError('unavailable', status, UNAVAILABLE, detail);
  }
  return new MasHistoryError('invalid', status, 'The request was rejected.', detail ?? problem.title ?? null);
}

@Injectable({
  providedIn: 'root'
})
export class MasHistoryService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(MAS_API_URL);

  /** A POST, so the search text stays out of the request line the gateway and the host log. */
  search(request: MasSearchRequestV1): Observable<MasSearchResponseV1> {
    return this.http
      .post<MasSearchResponseV1>(`${this.apiUrl}/messages/search`, request)
      .pipe(catchError((error) => this.errorHandler(error, 'search', 'Error searching the message history')));
  }

  getMessage(messageId: string): Observable<MasMessageV1> {
    return this.http
      .get<MasMessageV1>(`${this.apiUrl}/messages/${encodeURIComponent(messageId)}`)
      .pipe(catchError((error) => this.errorHandler(error, 'message', `Error getting message ${messageId}`)));
  }

  /** Every member and repository a message can be found by, for the list's pickers. */
  getFilters(): Observable<MasFiltersResponseV1> {
    return this.http
      .get<MasFiltersResponseV1>(`${this.apiUrl}/filters`)
      .pipe(catchError((error) => this.errorHandler(error, 'filters', 'Error getting the message history filters')));
  }

  /** The pages render each failure where it happened, so this reports to the caller instead of raising a toast. */
  private errorHandler(error: unknown, call: MasHistoryCall, serviceMessage: string): Observable<never> {
    const failure = toMasHistoryError(error, call);
    console.error(`[MasHistoryService] ${serviceMessage}, ${failure.status} ${failure.message}`);
    return throwError(() => failure);
  }
}
